// 首页 SPU 营销大卡：大图（约 60% 卡面）+ 名称 + 规格数 + 询价 CTA；桌面 hover 上浮
import { Box, Card, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { THEME_COLORS } from '@bluplanet/shared';
import { usePrefs } from '@/store/BuyerPrefsContext';
import type { BuyerSpu } from '@/utils/spuAdapter';

export default function ProductCard({ spu }: { spu: BuyerSpu }) {
  const navigate = useNavigate();
  const { t, toDisplay } = usePrefs();

  return (
    <Card
      onClick={() => navigate(`/buyer/products/${spu.id}`)}
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: `1px solid ${THEME_COLORS.border}`,
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 10px 24px rgba(5,150,105,0.16)',
        },
      }}
    >
      <Box
        sx={{
          height: { xs: 110, md: 150 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: { xs: '44px', md: '52px' },
          background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
          overflow: 'hidden',
        }}
      >
        {/* 封面回退链：SPU 封面 → 品类封面 → emoji */}
        {(() => {
          const cover = spu.coverImageUrl || spu.categoryCoverImageUrl;
          return cover ? (
            <img
              src={cover}
              alt={spu.name}
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            spu.emoji
          );
        })()}
      </Box>

      <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {spu.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: .75, mt: 0.5 }}>
          {spu.category ? (
            <Chip label={spu.category} title={spu.category} size="small" sx={{ maxWidth: '100%', height: 20, fontSize: '10px', bgcolor: '#ECFDF5', color: '#059669' }} />
          ) : (
            <Box />
          )}
          {/* 只有多规格才提示可选规格数；单规格不展示（对买家无信息量） */}
          {spu.skuCount != null && spu.skuCount > 1 && (
            <Typography sx={{ fontSize: '11px', color: '#757575' }}>
              {spu.skuCount} {t('home.specCount')}
            </Typography>
          )}
        </Box>
        {/* 行情裸价区间（后端聚合）；无有效价时占位「价格待询」，保持各卡片等高 */}
        {spu.priceMin != null && spu.priceMax != null && Number(spu.priceMin) > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#059669' }}>
              {toDisplay(spu.priceMin).symbol}
              {toDisplay(spu.priceMin).value.toLocaleString()}
              {spu.priceMax !== spu.priceMin ? `~${toDisplay(spu.priceMax).symbol}${toDisplay(spu.priceMax).value.toLocaleString()}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#9E9E9E' }}>{t('home.unitTon')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#BDBDBD' }}>{t('home.priceOnRequest')}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#059669', '&:hover': { textDecoration: 'underline' } }}>
            {t('home.inquire')} →
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
